import { COOKIE_NAME } from '@constants/cookies';

const expirationDaysDefault = 365;
const configDefault = { expires: expirationDaysDefault, path: '/' };

/**
 * Parses the document.cookie string and returns an object containing the cookies.
 *
 * @returns An object representing the cookies, where the keys are the cookie names and the values are the cookie values.
 */
export function getParsedCookies() {
    return document.cookie.split('; ').reduce((cookies, cookie) => {
        const [cookieName, cookieValue] = cookie.split('=');
        cookies[cookieName] = cookieValue;
        return cookies;
    }, {});
}

/**
 * Get cookie
 * @param {string} name           - Cookie name
 * @param {string} [defaultValue] - Default fallback value
 * @param {string} [cookie]       - Cookie content, if not provided document.cookie is used
 * @returns string
 */
export const get = (name, defaultValue = '', cookie) => {
    try {
        const myCookie = cookie || document.cookie;
        const regex = new RegExp(`${name}=([^;]*)`, 'i');
        const results = regex.exec(myCookie);

        return results ? results[1] : defaultValue;
    } catch (err) {
        return undefined;
    }
};

/**
 * Get URI decoded cookie
 * @param {string} name           - Cookie name
 * @param {string} [defaultValue] - Default fallback value
 * @param {string} [cookie]       - Cookie content, if not provided document.cookie is used
 * @returns string
 */
export const getDecodedCookie = (name, defaultValue = '', cookie) => {
    const unparsedCookie = get(name, defaultValue, cookie);

    try {
        return decodeURIComponent(unparsedCookie);
    } catch (err) {
        return unparsedCookie;
    }
};

/**
 * Get URL decoded and JSON parsed cookie
 * @param {string} name           - Cookie name
 * @param {string} [defaultValue] - Default fallback value
 * @param {string} [cookie]       - Cookie content, if not provided document.cookie is used
 * @returns {object}
 */
export const getParsedCookie = (name, defaultValue = '', cookie) => {
    const unparsedCookie = getDecodedCookie(name, defaultValue, cookie);

    try {
        return JSON.parse(unparsedCookie);
    } catch (err) {
        return unparsedCookie;
    }
};

/**
 * Set cookie
 * @param {string} name
 * @param {string} value
 * @param {object} config
 */
export const set = (name, value, config = {}) => {
    try {
        const {
            expires, expireDateString, path, domain
        } = { ...configDefault, ...config };

        let expiryDate = new Date();

        if (expireDateString) {
            const parsedDate = new Date(expireDateString);
            const isValidDate = !Number.isNaN(parsedDate.getTime());

            if (isValidDate) {
                expiryDate = parsedDate;
            }
        } else if (typeof expires === 'number') {
            expiryDate.setTime(expiryDate.getTime() + (expires * 24 * 60 * 60 * 1000));
        }

        const cookieParams = `${name}=${value}; expires=${expiryDate.toUTCString()}; path=${path};`;
        document.cookie = domain ? `${cookieParams} domain=${domain};` : cookieParams;
    } catch (err) {
        // ignore errors
    }
};

/**
 * Remove cookie by name
 * @param {string} name Name
 */
export const remove = (name) => {
    try {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    } catch (err) {
        // ignore errors
    }
};

export const isActiveMarketingGroup = () => {
    const optanonConsent = getParsedCookie(COOKIE_NAME.optanonConsent);
    return optanonConsent?.split('&')?.find(param => param.includes('groups='))?.includes('C0004:1');
};

export const isActiveFunctionalGroup = () => {
    const optanonConsent = getParsedCookie(COOKIE_NAME.optanonConsent);
    return optanonConsent?.split('&')?.find(param => param.includes('groups='))?.includes('C0003:1');
};

export const isActiveAnalyticsGroup = () => {
    const optanonConsent = getParsedCookie(COOKIE_NAME.optanonConsent);
    return optanonConsent?.split('&')?.find(param => param.includes('groups='))?.includes('C0002:1');
};
